import { FC, useEffect } from 'react';
import {
  AggregateEuRootRequest,
  useAggregateEuRoot,
} from 'src/apis/aggregate-eu/use-aggregate-eu-root';
import {
  OrganisationDetailsRequest,
  useOrganisationDetails,
} from 'src/apis/organisation-service/use-organisation-details';
import {
  OrganisationServiceRequest,
  useOrganisationService,
} from 'src/apis/organisation-service/use-organisation-service';
import { Stack } from 'src/components/layout/stack';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Modal } from 'src/components/overlay/modal';
import { Heading } from 'src/components/text/heading';
import { Successful } from 'src/hooks/use-axios';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useIsCypress } from 'src/hooks/use-is-cypress';
import { useModal2 } from 'src/hooks/use-modal';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useSafeLocalStorage } from 'src/hooks/use-storage';

export const WelcomeModal: FC = () => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const cognitoUser = useCognitoUser();
  const isCypress = useIsCypress();
  const loggedIn = Boolean(monolithUser || cognitoUser);

  if (!isCypress && loggedIn) return <WelcomeModalWithServices />;
  else return null;
};
export const WelcomeModalWithServices: FC = () => {
  const aggregateEuRootRequest = useAggregateEuRoot();
  const organisationService = useOrganisationService();

  if (!organisationService.response) {
    return null;
  }
  return (
    <WelcomeModalWithOrganisationDetails
      aggregateEuRootRequest={aggregateEuRootRequest}
      organisationService={organisationService}
    />
  );
};

const WelcomeModalWithOrganisationDetails: FC<{
  aggregateEuRootRequest: AggregateEuRootRequest;
  organisationService: Successful<OrganisationServiceRequest>;
}> = ({ aggregateEuRootRequest, organisationService }) => {
  const myOrganisationDetails = useOrganisationDetails({
    url: organisationService.response.data._links.getMyOrganisationDetails.href,
  });

  if (!myOrganisationDetails.response) {
    return null;
  }

  return (
    <Page
      aggregateEuRootRequest={aggregateEuRootRequest}
      myOrganisationDetails={myOrganisationDetails}
    />
  );
};

const Page: FC<{
  aggregateEuRootRequest: AggregateEuRootRequest;
  myOrganisationDetails: Successful<OrganisationDetailsRequest>;
}> = ({ aggregateEuRootRequest, myOrganisationDetails }) => {
  const safeLocalStorage = useSafeLocalStorage();
  const welcomeModal = useModal2({
    modal: (props) => {
      return <WelcomeModalContent {...props} />;
    },
    trigger: () => null,
  });

  const organisationHasRole = Boolean(
    myOrganisationDetails.response.data._links.shipperDetails ||
      myOrganisationDetails.response.data._links.ssoDetails ||
      myOrganisationDetails.response.data._links.lsoDetails ||
      myOrganisationDetails.response.data._links.tsoDetails
  );

  const organisationHasAggregateEuSubscription = Boolean(
    aggregateEuRootRequest.response?.data._links?.manageOrganisationSubscription
  );

  useEffect(() => {
    if (
      !organisationHasAggregateEuSubscription &&
      !organisationHasRole &&
      safeLocalStorage.getItem('checkWelcomeModal') === 'true'
    ) {
      safeLocalStorage.setItem('checkWelcomeModal', 'false');
      welcomeModal.open();
    }
  }, [
    welcomeModal,
    safeLocalStorage,
    aggregateEuRootRequest,
    organisationHasRole,
    organisationHasAggregateEuSubscription,
  ]);

  return welcomeModal.modal;
};

const WelcomeModalContent: FC<{
  close: () => void;
}> = ({ close }) => {
  return (
    <Modal
      data-testid="welcome-modal"
      title="Finish Registration Process"
      onDismiss={close}
      size="large"
      footer={null}
    >
      <Stack gap={1.5}>
        <Heading mode="card">Join the PRISMA Platform as a Shipper</Heading>
        <p>
          To join the transport, storage and/or LNG capacity markets, you must{' '}
          <Link
            mode="default-underlined"
            onClick={close}
            to="/settings/organisation/company-information/apply-for-shipper-role/address-details"
          >
            provide additional information
          </Link>
          . Furthermore, the infrastructure operators may require further
          documents to approve your assignment - that is, your request to
          establish a business relationship with them. You can find the complete
          list of operators with their required documents on our{' '}
          <Link
            mode="default-underlined"
            onClick={close}
            to="/market-information/players/operators"
          >
            Market Participants page
          </Link>
          . If you need further information about how to apply for an
          assignment, please check the following{' '}
          <ExternalLink
            target="_blank"
            mode="default-underlined"
            href="https://help.prisma-capacity.eu/support/solutions/articles/36000292870-how-to-request-an-assignment-for-one-of-my-shipper-users"
          >
            article
          </ExternalLink>
          .
        </p>

        <Heading mode="card">Eager to join AggregateEU?</Heading>
        <p>
          You can start the subscription process of AggregateEU{' '}
          <Link mode="default-underlined" onClick={close} to="/aggregate-eu/">
            here
          </Link>
          .
        </p>
        <Stack flow="column" gap={1} justifyContent="end" justifyItems="center">
          <Link
            to="/aggregate-eu/overview"
            mode="button-primary"
            onClick={close}
          >
            Subscribe to Aggregate EU
          </Link>

          <Link
            to="/settings/organisation/company-information/apply-for-shipper-role/"
            mode="button-primary"
            onClick={close}
          >
            Become a Shipper
          </Link>
        </Stack>
      </Stack>
    </Modal>
  );
};
